/**
 * Equal split of an ILS total across 1–4 credit-card charges.
 * Arithmetic is integer agorot; the last card absorbs the remainder so the
 * sum always equals the required total exactly.
 */
import { agorotToIls, ilsToAgorot } from "../publicMoneyAgorot";

export const SEQUENTIAL_MAX_CARDS = 4;

export function clampSequentialCardCount(cardCount) {
  const n = Math.floor(Number(cardCount) || 1);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(SEQUENTIAL_MAX_CARDS, n);
}

export function splitAgorotAcrossCards(totalAgorot, cardCount) {
  const total = Math.max(0, Math.round(Number(totalAgorot) || 0));
  const n = clampSequentialCardCount(cardCount);
  if (n === 1) return [total];
  const base = Math.floor(total / n);
  const remainder = total - base * n;
  const parts = [];
  for (let i = 0; i < n; i += 1) {
    parts.push(i === n - 1 ? base + remainder : base);
  }
  return parts;
}

export function splitIlsAcrossCards(totalIls, cardCount) {
  const totalA = ilsToAgorot(totalIls);
  if (totalA == null) return [];
  return splitAgorotAcrossCards(totalA, cardCount).map((a) => agorotToIls(a));
}

/**
 * Rebuild planned charge amounts: paid slots keep captured ILS; remaining
 * payable agorot are split across unpaid slots (last unpaid absorbs remainder).
 *
 * @returns {number[]} length === cardCount
 */
export function rebuildSequentialChargeAmounts({
  cardCount,
  payableIls,
  paidAmountsIls = [],
} = {}) {
  const n = clampSequentialCardCount(cardCount);
  const payableA = ilsToAgorot(payableIls);
  if (payableA == null) return Array.from({ length: n }, () => 0);

  const paidList = Array.isArray(paidAmountsIls) ? paidAmountsIls : [];
  const locked = Math.min(n, paidList.length);
  const paidA = paidList
    .slice(0, locked)
    .reduce((sum, v) => sum + (ilsToAgorot(v) || 0), 0);
  const remainingA = Math.max(0, payableA - paidA);
  const unpaidN = n - locked;
  const unpaidParts =
    unpaidN > 0 ? splitAgorotAcrossCards(remainingA, unpaidN) : [];

  const amounts = [];
  for (let i = 0; i < n; i += 1) {
    if (i < locked) {
      const capturedA = ilsToAgorot(paidList[i]);
      amounts.push(agorotToIls(capturedA == null ? 0 : capturedA));
    } else {
      amounts.push(agorotToIls(unpaidParts[i - locked]));
    }
  }
  return amounts;
}

export function sequentialAmountsMatch(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  return a.every((v, i) => (ilsToAgorot(v) || 0) === (ilsToAgorot(b[i]) || 0));
}
